import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useClockContext } from '../app';
import { LayoutSite } from '../components/layout';
import { get_analysis_datasinfos, get_analysis_fusiongps, get_analysis_trackergps, get_datas } from '../services/api.service';
import { ApiAnalysisDatasInfos, ApiGpsPoint } from '../types';
import './analysis.scss';

/* ══════════════════════════════════════════════════════════════════════════════
   Map helpers
══════════════════════════════════════════════════════════════════════════════ */

interface Viewport
{
    zoom: number;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
}

function mercatorNorm(lat: number): number
{
    const r = lat * Math.PI / 180;
    return (1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2;
}

function latlngToCanvas(lat: number, lng: number, vp: Viewport): { x: number; y: number }
{
    const scale = Math.pow(2, vp.zoom) * 256;
    return {
        x: (lng + 180) / 360 * scale - vp.offsetX,
        y: mercatorNorm(lat) * scale - vp.offsetY,
    };
}

function buildViewport(
    minLat: number, maxLat: number, minLng: number, maxLng: number,
    canvasW: number, canvasH: number,
): Viewport
{
    let zoom = 18;
    for (; zoom >= 0; zoom--)
    {
        const scale = Math.pow(2, zoom) * 256;
        const dx = (maxLng - minLng) / 360 * scale;
        const dy = (mercatorNorm(minLat) - mercatorNorm(maxLat)) * scale;
        if (dx <= canvasW * 0.90 && dy <= canvasH * 0.90) break;
    }
    const scale   = Math.pow(2, zoom) * 256;
    const wx1     = (minLng + 180) / 360 * scale;
    const wx2     = (maxLng + 180) / 360 * scale;
    const wy1     = mercatorNorm(maxLat) * scale;
    const wy2     = mercatorNorm(minLat) * scale;
    const offsetX = wx1 - (canvasW - (wx2 - wx1)) / 2;
    const offsetY = wy1 - (canvasH - (wy2 - wy1)) / 2;
    return { zoom, offsetX, offsetY, width: canvasW, height: canvasH };
}

/* ── Tile cache ───────────────────────────────────────────────────────────── */
const tileCache = new Map<string, HTMLImageElement>();
const TILE_SUB  = ['a', 'b', 'c'];

/**
 * Draw tiles from the cache onto ctx.
 * onTileLoad is called when any uncached tile finishes loading.
 * Pass null to skip registering callbacks (draw-only mode).
 */
function drawTiles(
    ctx: CanvasRenderingContext2D,
    vp: Viewport,
    onTileLoad: (() => void) | null,
): void
{
    const TILE = 256;
    const maxT = Math.pow(2, vp.zoom);
    const tx0  = Math.floor(vp.offsetX / TILE);
    const ty0  = Math.floor(vp.offsetY / TILE);
    const tx1  = Math.ceil((vp.offsetX + vp.width)  / TILE);
    const ty1  = Math.ceil((vp.offsetY + vp.height) / TILE);

    for (let tx = tx0; tx < tx1; tx++)
    {
        for (let ty = ty0; ty < ty1; ty++)
        {
            if (ty < 0 || ty >= maxT) continue;
            const txw = ((tx % maxT) + maxT) % maxT;
            const key = `${vp.zoom}/${txw}/${ty}`;
            const dx  = Math.round(tx * TILE - vp.offsetX);
            const dy  = Math.round(ty * TILE - vp.offsetY);

            let img = tileCache.get(key);
            if (!img)
            {
                img = new Image();
                img.crossOrigin = 'anonymous';
                img.src = `https://${TILE_SUB[(txw + ty) % 3]}.tile.openstreetmap.org/${vp.zoom}/${txw}/${ty}.png`;
                if (onTileLoad) img.onload = onTileLoad;
                tileCache.set(key, img);
            }
            else if (onTileLoad && !img.complete)
            {
                // Keep the latest callback on in-flight tiles
                img.onload = onTileLoad;
            }
            if (img.complete && img.naturalWidth > 0)
            {
                ctx.drawImage(img, dx, dy, TILE, TILE);
            }
        }
    }
}

/* ── Track colors ─────────────────────────────────────────────────────────── */
const TRACK_COLORS = [
    '#e74c3c','#3498db','#fd79a8','#f39c12',
    '#9b59b6','#6c5ce7','#e67e22','#e91e63',
    '#00bcd4','#d63031','#ff5722','#607d8b',
];
const trackerColor = (n: number) => TRACK_COLORS[Math.abs(n) % TRACK_COLORS.length];

const FUSION_COLORS = [
    '#ff6b6b','#ffa94d','#ffd43b','#e599f7',
    '#4dabf7','#da77f2','#f783ac','#ff9f43',
    '#748ffc','#ff8787','#74c0fc','#fcc2d7',
];
const fusionColor = (fid: number) => FUSION_COLORS[Math.abs(fid) % FUSION_COLORS.length];

/* ── Track rendering ──────────────────────────────────────────────────────── */
function drawTrack(
    ctx: CanvasRenderingContext2D, vp: Viewport,
    points: ApiGpsPoint[], color: string, currentTime: number,
    showDots: boolean, dashed = false,
): void
{
    if (!points.length) return;

    let cur = 0;
    for (let i = 0; i < points.length; i++)
    {
        if (points[i].ts <= currentTime) cur = i; else break;
    }

    // Played part only (up to cursor)
    if (cur > 0)
    {
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth   = dashed ? 1.5 : 2.5;
        ctx.lineJoin    = 'round';
        if (dashed) ctx.setLineDash([6, 4]);
        for (let i = 0; i <= cur; i++)
        {
            const { x, y } = latlngToCanvas(points[i].lat, points[i].lng, vp);
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
        if (dashed) ctx.setLineDash([]);
    }

    // Small dot at every played data point
    if (showDots)
    {
        ctx.fillStyle = color + 'cc';
        for (let i = 0; i <= cur; i++)
        {
            const { x, y } = latlngToCanvas(points[i].lat, points[i].lng, vp);
            ctx.beginPath();
            ctx.arc(x, y, 2.5, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    // Current-position dot
    const { x, y } = latlngToCanvas(points[cur].lat, points[cur].lng, vp);
    ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2); ctx.fillStyle = color; ctx.fill();
    ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2); ctx.strokeStyle = 'white'; ctx.lineWidth = 2; ctx.stroke();
}

/* ── Formatting ───────────────────────────────────────────────────────────── */
function fmtDuration(ms: number): string
{
    const s   = Math.floor(Math.abs(ms) / 1000);
    const h   = Math.floor(s / 3600);
    const m   = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return [h, m, sec].map(v => String(v).padStart(2, '0')).join(':');
}

/* ══════════════════════════════════════════════════════════════════════════════
   Component
══════════════════════════════════════════════════════════════════════════════ */

export const AnalysisPage: FC = () =>
{
    const clock = useClockContext();

    /* ── UI state ──────────────────────────────────────────────────────────── */
    const [allDatas,    setAllDatas]    = useState<string[]>([]);
    const [seldatas,    setSelDatas]    = useState<string>('');
    const [moteur,      setMoteur]      = useState<ApiAnalysisDatasInfos | null>(null);
    const [expFusions,      setExpFusions]      = useState<Set<number>>(new Set());
    const [selTrackers,     setSelTrackers]     = useState<Set<number>>(new Set());
    const [selFusionTracks, setSelFusionTracks] = useState<Set<string>>(new Set());
    const [playing,     setPlaying]     = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [timeRange,   setTimeRange]   = useState({ min: 0, max: 0 });
    const [speed,       setSpeed]       = useState(10);
    const [markIn,      setMarkIn]      = useState<number | null>(null);
    const [markOut,     setMarkOut]     = useState<number | null>(null);
    const [showDots,    setShowDots]    = useState(false);

    /* ── Refs ──────────────────────────────────────────────────────────────── */
    const canvasRef         = useRef<HTMLCanvasElement>(null);
    const containerRef      = useRef<HTMLDivElement>(null);
    const gpsDataRef        = useRef<Map<number, ApiGpsPoint[]>>(new Map());
    const fusionGpsDataRef  = useRef<Map<string, ApiGpsPoint[]>>(new Map());
    const viewportRef       = useRef<Viewport | null>(null);
    // baseViewport = the auto-fit view; defines zoom-out limit and bg tile set
    const baseViewportRef   = useRef<Viewport | null>(null);
    // offscreen canvas holding the static tile background at baseViewport
    const bgCanvasRef       = useRef<HTMLCanvasElement | null>(null);
    const currentTimeRef    = useRef(0);
    const timeRangeRef      = useRef({ min: 0, max: 0 });
    const speedRef          = useRef(10);
    const playingRef        = useRef(false);
    const rafIdRef          = useRef<number | null>(null);
    const lastRafTimeRef    = useRef(0);
    const activeDatasetRef  = useRef('');
    const markInRef         = useRef<number | null>(null);
    const markOutRef        = useRef<number | null>(null);
    const manualViewportRef = useRef(false);
    const dragRef           = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
    const showDotsRef       = useRef(false);

    /* ── Sync refs ─────────────────────────────────────────────────────────── */
    useEffect(() => { playingRef.current = playing; }, [playing]);
    useEffect(() => { speedRef.current   = speed;   }, [speed]);
    useEffect(() => { markInRef.current   = markIn;    }, [markIn]);
    useEffect(() => { markOutRef.current  = markOut;   }, [markOut]);
    useEffect(() => { showDotsRef.current = showDots;  }, [showDots]);

    /* ── redraw — scales the static bg image, then draws GPS tracks ─────────── */
    const redraw = useCallback(() =>
    {
        const canvas   = canvasRef.current;
        const vp       = viewportRef.current;
        const baseVp   = baseViewportRef.current;
        const bgCanvas = bgCanvasRef.current;
        if (!canvas || !vp) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw the static background scaled to the current zoom
        if (bgCanvas && baseVp)
        {
            const f    = Math.pow(2, vp.zoom - baseVp.zoom);
            const dstX = baseVp.offsetX * f - vp.offsetX;
            const dstY = baseVp.offsetY * f - vp.offsetY;
            ctx.drawImage(bgCanvas, dstX, dstY, bgCanvas.width * f, bgCanvas.height * f);
        }

        const mIn  = markInRef.current;
        const mOut = markOutRef.current;

        const filterPts = (pts: ApiGpsPoint[]) =>
            (mIn !== null || mOut !== null)
                ? pts.filter(p => (mIn === null || p.ts >= mIn) && (mOut === null || p.ts <= mOut))
                : pts;

        gpsDataRef.current.forEach((points, tracker) =>
        {
            const visible = filterPts(points);
            if (visible.length) drawTrack(ctx, vp, visible, trackerColor(tracker), currentTimeRef.current, showDotsRef.current);
        });

        fusionGpsDataRef.current.forEach((points, key) =>
        {
            const isRaw = key.startsWith('raw-');
            const fid   = parseInt(key.split('-')[1], 10);
            const visible = filterPts(points);
            if (visible.length) drawTrack(ctx, vp, visible, fusionColor(fid), currentTimeRef.current, showDotsRef.current, isRaw);
        });
    }, []); // only reads refs and module-level fns

    /* ── renderBg — paints tiles onto the offscreen canvas ──────────────────── */
    // Called when a tile finishes loading: re-paint the bgCanvas with whatever
    // is cached now (null = no new callbacks, avoids self-referential recursion).
    const onTileLoaded = useCallback(() =>
    {
        const bgCanvas = bgCanvasRef.current;
        const baseVp   = baseViewportRef.current;
        if (!bgCanvas || !baseVp) return;
        const ctx = bgCanvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, bgCanvas.width, bgCanvas.height);
        drawTiles(ctx, baseVp, null); // draw cached tiles only, no new callbacks
        redraw();
    }, [redraw]);

    const renderBg = useCallback(() =>
    {
        const bgCanvas = bgCanvasRef.current;
        const baseVp   = baseViewportRef.current;
        if (!bgCanvas || !baseVp) return;
        const ctx = bgCanvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, bgCanvas.width, bgCanvas.height);
        drawTiles(ctx, baseVp, onTileLoaded); // register onTileLoaded for in-flight tiles
        redraw();
    }, [redraw, onTileLoaded]);

    /* ── recomputeViewport — rebuilds baseVp + bg from data / marks ─────────── */
    const recomputeViewport = useCallback(() =>
    {
        const canvas = canvasRef.current;
        if (!canvas || canvas.width === 0 || canvas.height === 0) return;

        const rawIn  = markInRef.current;
        const rawOut = markOutRef.current;
        const mIn    = rawIn  !== null && rawOut !== null ? Math.min(rawIn, rawOut) : rawIn;
        const mOut   = rawIn  !== null && rawOut !== null ? Math.max(rawIn, rawOut) : rawOut;

        // Collect visible points — avoid spread operator on large arrays (stack overflow)
        let minLat =  Infinity, maxLat = -Infinity;
        let minLng =  Infinity, maxLng = -Infinity;
        let hasPoints = false;

        const accumBounds = (pts: ApiGpsPoint[]) =>
        {
            const arr = (mIn !== null || mOut !== null)
                ? pts.filter(p => (mIn === null || p.ts >= mIn) && (mOut === null || p.ts <= mOut))
                : pts;
            for (let i = 0; i < arr.length; i++)
            {
                const p = arr[i];
                if (p.lat < minLat) minLat = p.lat;
                if (p.lat > maxLat) maxLat = p.lat;
                if (p.lng < minLng) minLng = p.lng;
                if (p.lng > maxLng) maxLng = p.lng;
                hasPoints = true;
            }
        };
        gpsDataRef.current.forEach(accumBounds);
        fusionGpsDataRef.current.forEach(accumBounds);

        if (!hasPoints)
        {
            viewportRef.current = null;
            baseViewportRef.current = null;
            return;
        }

        const dLat = Math.max((maxLat - minLat) * 0.1, 0.0005);
        const dLng = Math.max((maxLng - minLng) * 0.1, 0.0005);
        minLat -= dLat; maxLat += dLat;
        minLng -= dLng; maxLng += dLng;

        const vp = buildViewport(minLat, maxLat, minLng, maxLng, canvas.width, canvas.height);
        viewportRef.current     = vp;
        baseViewportRef.current = vp;

        // (Re)create the background offscreen canvas at the base viewport size
        if (!bgCanvasRef.current) bgCanvasRef.current = document.createElement('canvas');
        bgCanvasRef.current.width  = vp.width;
        bgCanvasRef.current.height = vp.height;

        renderBg();
    }, [renderBg]);

    /* ── Canvas resize ─────────────────────────────────────────────────────── */
    useEffect(() =>
    {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries =>
        {
            const { width, height } = entries[0].contentRect;
            const canvas = canvasRef.current;
            if (!canvas) return;
            const newW = Math.floor(width);
            const newH = Math.floor(height);
            canvas.width  = newW;
            canvas.height = newH;
            if (manualViewportRef.current && viewportRef.current)
            {
                // Keep geographic centre, just adapt canvas size
                const vp = viewportRef.current;
                const cx = vp.offsetX + vp.width  / 2;
                const cy = vp.offsetY + vp.height / 2;
                viewportRef.current = { ...vp, offsetX: cx - newW / 2, offsetY: cy - newH / 2, width: newW, height: newH };
                redraw();
            }
            else
            {
                recomputeViewport();
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [redraw, recomputeViewport]);

    /* ── Animation ─────────────────────────────────────────────────────────── */
    const animStep = useCallback((now: number) =>
    {
        const dt      = lastRafTimeRef.current ? now - lastRafTimeRef.current : 0;
        lastRafTimeRef.current = now;
        const newTime = Math.min(currentTimeRef.current + dt * speedRef.current, timeRangeRef.current.max);
        currentTimeRef.current = newTime;
        setCurrentTime(newTime);
        redraw();
        if (newTime < timeRangeRef.current.max) { rafIdRef.current = requestAnimationFrame(animStep); }
        else { playingRef.current = false; setPlaying(false); }
    }, [redraw]);

    const play = useCallback(() =>
    {
        if (currentTimeRef.current >= timeRangeRef.current.max)
        {
            currentTimeRef.current = timeRangeRef.current.min;
            setCurrentTime(timeRangeRef.current.min);
        }
        playingRef.current = true; setPlaying(true);
        lastRafTimeRef.current = 0;
        rafIdRef.current = requestAnimationFrame(animStep);
    }, [animStep]);

    const pause = useCallback(() =>
    {
        playingRef.current = false; setPlaying(false);
        if (rafIdRef.current !== null) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = null; }
    }, []);

    const seekTo = useCallback((t: number) =>
    {
        currentTimeRef.current = t; setCurrentTime(t); redraw();
    }, [redraw]);

    /* ── Load dataset list ─────────────────────────────────────────────────── */
    useEffect(() =>
    {
        get_datas().then(setAllDatas).catch(e => message.error(String(e)));
    }, []);

    /* ── Select dataset ────────────────────────────────────────────────────── */
    useEffect(() =>
    {
        if (!seldatas) return;
        pause();
        activeDatasetRef.current    = seldatas;
        setMoteur(null); setSelTrackers(new Set()); setSelFusionTracks(new Set());
        setTimeRange({ min: 0, max: 0 }); setCurrentTime(0);
        setMarkIn(null); setMarkOut(null);
        gpsDataRef.current          = new Map();
        fusionGpsDataRef.current    = new Map();
        viewportRef.current         = null;
        baseViewportRef.current     = null;
        bgCanvasRef.current         = null;
        timeRangeRef.current        = { min: 0, max: 0 };
        currentTimeRef.current      = 0;
        markInRef.current           = null;
        markOutRef.current          = null;
        manualViewportRef.current   = false;

        get_analysis_datasinfos(seldatas)
            .then(m =>
            {
                if (!m) return;
                setMoteur(m);
                const fusionsWithGps = m.liste_fusion.filter((fid, i) =>
                    (m.id_fusion[i] ?? []).some(t => t !== -1 && m.trackers_with_gps.includes(t))
                );
                setExpFusions(new Set(fusionsWithGps));
            })
            .catch(e => message.error(String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seldatas]); // pause is useCallback([]) → stable, intentionally omitted

    /* ── Toggle tracker ────────────────────────────────────────────────────── */
    const toggleTracker = useCallback((tracker: number, isSelected: boolean) =>
    {
        if (isSelected)
        {
            setSelTrackers(prev => { const s = new Set(prev); s.delete(tracker); return s; });
            const next = new Map(gpsDataRef.current); next.delete(tracker);
            gpsDataRef.current = next;
            if (viewportRef.current) redraw(); else recomputeViewport();
        }
        else
        {
            setSelTrackers(prev => { const s = new Set(prev); s.add(tracker); return s; });
            const capturedDataset = seldatas;
            get_analysis_trackergps(seldatas, tracker)
                .then(pts =>
                {
                    if (activeDatasetRef.current !== capturedDataset) return;
                    const next = new Map(gpsDataRef.current); next.set(tracker, pts);
                    gpsDataRef.current = next;
                    if (pts.length)
                    {
                        const pMin = pts[0].ts, pMax = pts[pts.length - 1].ts;
                        timeRangeRef.current = {
                            min: timeRangeRef.current.min === 0 ? pMin : Math.min(timeRangeRef.current.min, pMin),
                            max: Math.max(timeRangeRef.current.max, pMax),
                        };
                        setTimeRange({ ...timeRangeRef.current });
                        if (currentTimeRef.current === 0 || currentTimeRef.current < timeRangeRef.current.min)
                        {
                            currentTimeRef.current = timeRangeRef.current.min;
                            setCurrentTime(timeRangeRef.current.min);
                        }
                    }
                    if (viewportRef.current) redraw(); else recomputeViewport();
                })
                .catch(e => message.error(String(e)));
        }
    }, [seldatas, redraw, recomputeViewport]);

    const toggleFusion = useCallback((id: number) =>
    {
        setExpFusions(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
    }, []);

    const toggleFusionTrack = useCallback((key: string, isSelected: boolean) =>
    {
        if (isSelected)
        {
            setSelFusionTracks(prev => { const s = new Set(prev); s.delete(key); return s; });
            const next = new Map(fusionGpsDataRef.current); next.delete(key);
            fusionGpsDataRef.current = next;
            if (viewportRef.current) redraw(); else recomputeViewport();
        }
        else
        {
            setSelFusionTracks(prev => { const s = new Set(prev); s.add(key); return s; });
            const capturedDataset = seldatas;
            const [type, fidStr]  = key.split('-');
            const fid = parseInt(fidStr, 10);
            get_analysis_fusiongps(seldatas, fid, type as 'raw' | 'fused')
                .then(pts =>
                {
                    if (activeDatasetRef.current !== capturedDataset) return;
                    const next = new Map(fusionGpsDataRef.current); next.set(key, pts);
                    fusionGpsDataRef.current = next;
                    if (pts.length)
                    {
                        const pMin = pts[0].ts, pMax = pts[pts.length - 1].ts;
                        timeRangeRef.current = {
                            min: timeRangeRef.current.min === 0 ? pMin : Math.min(timeRangeRef.current.min, pMin),
                            max: Math.max(timeRangeRef.current.max, pMax),
                        };
                        setTimeRange({ ...timeRangeRef.current });
                        if (currentTimeRef.current === 0 || currentTimeRef.current < timeRangeRef.current.min)
                        {
                            currentTimeRef.current = timeRangeRef.current.min;
                            setCurrentTime(timeRangeRef.current.min);
                        }
                    }
                    if (viewportRef.current) redraw(); else recomputeViewport();
                })
                .catch(e => message.error(String(e)));
        }
    }, [seldatas, redraw, recomputeViewport]);

    /* ── Select / deselect all trackers ────────────────────────────────────── */
    const selectAllTrackers = useCallback(() =>
    {
        if (!moteur) return;
        const capturedDataset = seldatas;
        moteur.trackers_with_gps.forEach(tracker =>
        {
            if (gpsDataRef.current.has(tracker)) return; // already loaded
            setSelTrackers(prev => { const s = new Set(prev); s.add(tracker); return s; });
            get_analysis_trackergps(seldatas, tracker)
                .then(pts =>
                {
                    if (activeDatasetRef.current !== capturedDataset) return;
                    const next = new Map(gpsDataRef.current); next.set(tracker, pts);
                    gpsDataRef.current = next;
                    if (pts.length)
                    {
                        const pMin = pts[0].ts, pMax = pts[pts.length - 1].ts;
                        timeRangeRef.current = {
                            min: timeRangeRef.current.min === 0 ? pMin : Math.min(timeRangeRef.current.min, pMin),
                            max: Math.max(timeRangeRef.current.max, pMax),
                        };
                        setTimeRange({ ...timeRangeRef.current });
                        if (currentTimeRef.current === 0 || currentTimeRef.current < timeRangeRef.current.min)
                        {
                            currentTimeRef.current = timeRangeRef.current.min;
                            setCurrentTime(timeRangeRef.current.min);
                        }
                    }
                    if (viewportRef.current) redraw(); else recomputeViewport();
                })
                .catch(e => message.error(String(e)));
        });
    }, [moteur, seldatas, redraw, recomputeViewport]);

    const deselectAllTrackers = useCallback(() =>
    {
        setSelTrackers(new Set());
        setSelFusionTracks(new Set());
        gpsDataRef.current       = new Map();
        fusionGpsDataRef.current = new Map();
        recomputeViewport();
    }, [recomputeViewport]);

    /* ── Mark in / out ─────────────────────────────────────────────────────── */
    const doSetMarkIn = useCallback(() =>
    {
        markInRef.current = currentTimeRef.current; setMarkIn(currentTimeRef.current);
        recomputeViewport();
    }, [recomputeViewport]);

    const doSetMarkOut = useCallback(() =>
    {
        markOutRef.current = currentTimeRef.current; setMarkOut(currentTimeRef.current);
        recomputeViewport();
    }, [recomputeViewport]);

    const clearMarks = useCallback(() =>
    {
        markInRef.current = null; markOutRef.current = null;
        setMarkIn(null); setMarkOut(null);
        recomputeViewport();
    }, [recomputeViewport]);

    /* ── Zoom helpers ──────────────────────────────────────────────────────── */
    const zoomTo = useCallback((dz: number, cx: number, cy: number) =>
    {
        const vp     = viewportRef.current;
        const baseVp = baseViewportRef.current;
        if (!vp || !baseVp) return;
        const zMin    = baseVp.zoom;
        const newZoom = Math.max(zMin, Math.min(28, vp.zoom + dz));
        if (newZoom === vp.zoom) return;
        const f = Math.pow(2, newZoom - vp.zoom);
        viewportRef.current = {
            zoom: newZoom,
            offsetX: (vp.offsetX + cx) * f - cx,
            offsetY: (vp.offsetY + cy) * f - cy,
            width:  vp.width,
            height: vp.height,
        };
        manualViewportRef.current = true;
        redraw();
    }, [redraw]);

    /* ── Wheel zoom ────────────────────────────────────────────────────────── */
    const handleWheel = useCallback((e: WheelEvent) =>
    {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect   = canvas.getBoundingClientRect();
        const cx     = (e.clientX - rect.left) * (canvas.width  / rect.width);
        const cy     = (e.clientY - rect.top)  * (canvas.height / rect.height);
        zoomTo(e.deltaY < 0 ? 1 : -1, cx, cy);
    }, [zoomTo]);

    /* ── Pan ───────────────────────────────────────────────────────────────── */
    const handleMouseDown = useCallback((e: MouseEvent) =>
    {
        if (!viewportRef.current) return;
        dragRef.current = { x: e.clientX, y: e.clientY, ox: viewportRef.current.offsetX, oy: viewportRef.current.offsetY };
        if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) =>
    {
        const d = dragRef.current;
        if (!d || !viewportRef.current) return;
        viewportRef.current = { ...viewportRef.current, offsetX: d.ox - (e.clientX - d.x), offsetY: d.oy - (e.clientY - d.y) };
        manualViewportRef.current = true;
        redraw();
    }, [redraw]);

    const handleMouseUp = useCallback(() =>
    {
        dragRef.current = null;
        if (containerRef.current) containerRef.current.style.cursor = 'grab';
    }, []);

    /* ── Fit view ──────────────────────────────────────────────────────────── */
    const fitView = useCallback(() =>
    {
        manualViewportRef.current = false;
        recomputeViewport();
    }, [recomputeViewport]);

    /* ── Event listeners ───────────────────────────────────────────────────── */
    useEffect(() =>
    {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.addEventListener('wheel',       handleWheel,     { passive: false });
        canvas.addEventListener('mousedown',   handleMouseDown);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup',   handleMouseUp);
        return () =>
        {
            canvas.removeEventListener('wheel',       handleWheel);
            canvas.removeEventListener('mousedown',   handleMouseDown);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup',   handleMouseUp);
        };
    }, [handleWheel, handleMouseDown, handleMouseMove, handleMouseUp]);

    /* ── Derived render values ─────────────────────────────────────────────── */
    const elapsed = currentTime - timeRange.min;
    const total   = timeRange.max - timeRange.min;

    const markInPct    = total > 0 && markIn  !== null ? (markIn  - timeRange.min) / total * 100 : null;
    const markOutPct   = total > 0 && markOut !== null ? (markOut - timeRange.min) / total * 100 : null;
    const overlayLeft  = markInPct !== null && markOutPct !== null ? Math.min(markInPct, markOutPct) : markInPct  ?? 0;
    const overlayRight = markInPct !== null && markOutPct !== null ? Math.max(markInPct, markOutPct) : markOutPct ?? 100;
    const showOverlay  = markInPct !== null && markOutPct !== null && total > 0;
    const hasMarks     = markIn !== null || markOut !== null;

    /* ── Render ────────────────────────────────────────────────────────────── */
    return (
        <LayoutSite>
            <div className="analysis">

                <div className="analysis__header">
                    <span>Analysis</span>
                    <select className="analysis__header__select" value={seldatas}
                        onChange={e => { pause(); setSelDatas(e.target.value); }}>
                        <option value="">— select dataset —</option>
                        {allDatas.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <div className="analysis__header__clock">{clock}</div>
                </div>

                <div className="analysis__body">

                    <div className="analysis__body__panel">
                        {moteur && (
                            <div className="analysis__body__panel__toolbar">
                                <button className="analysis__body__panel__toolbar__btn"
                                    onClick={selectAllTrackers}
                                    disabled={!moteur.trackers_with_gps.length}
                                    title="Select all trackers">☑</button>
                                <button className="analysis__body__panel__toolbar__btn"
                                    onClick={deselectAllTrackers}
                                    disabled={selTrackers.size === 0 && selFusionTracks.size === 0}
                                    title="Deselect all">☐</button>
                            </div>
                        )}
                        {moteur && moteur.liste_fusion
                            .map((fid, i) => ({ fid, trackers: (moteur.id_fusion[i] ?? []).filter(t => t !== -1) }))
                            .sort((a, b) => a.fid - b.fid)
                            .map(({ fid, trackers }) => (
                                <div key={fid} className="analysis__body__panel__fusion">
                                    <div className="analysis__body__panel__fusion__header" onClick={() => toggleFusion(fid)}>
                                        <span className="analysis__body__panel__fusion__header__arrow">
                                            {expFusions.has(fid) ? '▾' : '▸'}
                                        </span>
                                        <span>Fusion #{fid}</span>
                                    </div>
                                    {expFusions.has(fid) && (
                                        <>
                                            {(['raw', 'fus'] as const).map(type =>
                                            {
                                                const key      = `${type}-${fid}`;
                                                const active   = selFusionTracks.has(key);
                                                const hasData  = moteur!.fusions_with_data.includes(fid);
                                                const modifier = !hasData ? '--disabled' : active ? '--active' : '';
                                                return (
                                                    <div key={type}
                                                        className={`analysis__body__panel__fusion__track${modifier}`}
                                                        onClick={() => hasData && toggleFusionTrack(key, active)}>
                                                        <span className="analysis__body__panel__fusion__track__dot"
                                                            style={{
                                                                background:  active ? fusionColor(fid) : 'transparent',
                                                                borderStyle: type === 'raw' ? 'dashed' : 'solid',
                                                            }} />
                                                        <span>{type === 'raw' ? 'Raw GPS' : 'Fused'}</span>
                                                    </div>
                                                );
                                            })}
                                            {trackers.map(t =>
                                            {
                                                const active   = selTrackers.has(t);
                                                const hasGps   = moteur!.trackers_with_gps.includes(t);
                                                const modifier = !hasGps ? '--disabled' : active ? '--active' : '';
                                                return (
                                                    <div key={t}
                                                        className={`analysis__body__panel__fusion__tracker${modifier}`}
                                                        onClick={() => hasGps && toggleTracker(t, active)}>
                                                        <span className="analysis__body__panel__fusion__tracker__dot"
                                                            style={{ background: active ? trackerColor(t) : 'transparent' }} />
                                                        <span>#{t}</span>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}
                                </div>
                            ))
                        }
                    </div>

                    <div className="analysis__body__map" ref={containerRef}>
                        <canvas ref={canvasRef} />
                        <div className="analysis__body__map__controls">
                            <button className="analysis__body__map__btn" title="Zoom in"
                                onClick={() => { const vp = viewportRef.current; if (vp) zoomTo(1, vp.width / 2, vp.height / 2); }}>+</button>
                            <button className="analysis__body__map__btn" title="Zoom out (limited to initial zone)"
                                onClick={() => { const vp = viewportRef.current; if (vp) zoomTo(-1, vp.width / 2, vp.height / 2); }}>−</button>
                            <button className="analysis__body__map__btn" title="Fit view to data" onClick={fitView}>⊡</button>
                            <button
                                className={`analysis__body__map__btn${showDots ? ' analysis__body__map__btn--active' : ''}`}
                                title={showDots ? 'Hide data points' : 'Show data points'}
                                onClick={() => { const next = !showDotsRef.current; showDotsRef.current = next; setShowDots(next); redraw(); }}
                            >·</button>
                        </div>
                    </div>
                </div>

                <div className="analysis__timeline">
                    <button className="analysis__timeline__btn" disabled={!total}
                        onClick={() => { pause(); seekTo(timeRange.min); }} title="Go to start">|◀</button>
                    <button className="analysis__timeline__btn analysis__timeline__btn--step" disabled={!total}
                        onClick={() => { pause(); seekTo(Math.max(timeRange.min, currentTime - 100)); }} title="−100 ms">◀</button>
                    <button className="analysis__timeline__btn analysis__timeline__btn--play" disabled={!total}
                        onClick={playing ? pause : play}>{playing ? '⏸' : '▶'}</button>
                    <button className="analysis__timeline__btn analysis__timeline__btn--step" disabled={!total}
                        onClick={() => { pause(); seekTo(Math.min(timeRange.max, currentTime + 100)); }} title="+100 ms">▶</button>
                    <button className="analysis__timeline__btn" disabled={!total}
                        onClick={() => { pause(); seekTo(timeRange.max); }} title="Go to end">▶|</button>

                    <span className="analysis__timeline__sep" />

                    <button
                        className={`analysis__timeline__btn${markIn !== null ? ' analysis__timeline__btn--marked' : ''}`}
                        disabled={!total} onClick={doSetMarkIn}
                        title={markIn !== null ? `Mark in: ${fmtDuration(markIn - timeRange.min)}` : 'Set mark in'}>⌊</button>
                    <button
                        className={`analysis__timeline__btn${markOut !== null ? ' analysis__timeline__btn--marked' : ''}`}
                        disabled={!total} onClick={doSetMarkOut}
                        title={markOut !== null ? `Mark out: ${fmtDuration(markOut - timeRange.min)}` : 'Set mark out'}>⌉</button>
                    <button className="analysis__timeline__btn" disabled={!hasMarks}
                        onClick={clearMarks} title="Clear marks">✕</button>

                    {hasMarks && total > 0 && (
                        <span className="analysis__timeline__marklabels">
                            {markIn  !== null ? fmtDuration(markIn  - timeRange.min) : '—'}
                            {' – '}
                            {markOut !== null ? fmtDuration(markOut - timeRange.min) : '—'}
                        </span>
                    )}

                    <span className="analysis__timeline__sep" />

                    <div className="analysis__timeline__scrubber-wrap">
                        {showOverlay && (
                            <div className="analysis__timeline__scrubber-marks"
                                style={{ left: `${overlayLeft}%`, width: `${overlayRight - overlayLeft}%` }} />
                        )}
                        <input type="range" className="analysis__timeline__scrubber"
                            min={timeRange.min} max={timeRange.max || timeRange.min + 1} step={500}
                            value={currentTime || timeRange.min} disabled={!total}
                            onChange={e => { pause(); seekTo(Number(e.target.value)); }} />
                    </div>

                    <span className="analysis__timeline__time">
                        {total > 0 ? `${fmtDuration(elapsed)} / ${fmtDuration(total)}` : '--:--:-- / --:--:--'}
                    </span>

                    <select className="analysis__timeline__speed" value={speed}
                        onChange={e => setSpeed(Number(e.target.value))}>
                        {[1, 5, 10, 30, 60].map(s => <option key={s} value={s}>{s}×</option>)}
                    </select>
                </div>

            </div>
        </LayoutSite>
    );
};
